import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import { ChevronDown, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Input } from './Input';
import { MultiSelectChips } from './MultiSelectChips';
import { Button, ButtonVariant, ButtonSize } from '@/shared/ui/Button';

export interface Option {
  id: string | number;
  name: string;
  /** Optional secondary label shown below the primary name in dropdown options */
  sublabel?: string;
}

interface SearchableSelectProps {
  id?: string;
  options: Option[];
  value: Option | Option[] | null;
  onChange: (value: Option | Option[] | null) => void;
  placeholder?: string;
  'data-testid'?: string;
  multiple?: boolean;
  disabled?: boolean;
  renderOption?: (option: any) => React.ReactNode;
  onLoadMore?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  /** When true, shows "Searching..." instead of "No results" — use when options are being fetched externally */
  isLoading?: boolean;
  maxSelections?: number;
  showCheckbox?: boolean;
  showClearButton?: boolean;
  /** Hide the search input inside the dropdown */
  showSearch?: boolean;
  /** Called whenever the search input changes — use for server-side search */
  onSearch?: (term: string) => void;
  /** Render the dropdown via a portal into document.body to escape overflow clipping */
  usePortal?: boolean;
  /**
   * Minimum height (px) for the portal dropdown list.
   * Overrides the auto-computed maxHeight floor so the list is never
   * shorter than this value (e.g. use 400 to ensure more options are visible).
   */
  minDropdownHeight?: number;
  /** Optional CSS class applied to the selected-value <span> in the trigger button */
  valueClassName?: string;
  /**
   * When provided, each option is wrapped in an <a> tag pointing to the URL
   * returned for that option. Plain click toggles selection; Ctrl/Cmd/middle-click
   * opens the entity detail page in a new tab — matching legacy DRT behaviour.
   */
  getItemUrl?: (item: Option) => string | undefined;
}

export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  id,
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  'data-testid': dataTestId,
  multiple = false,
  disabled = false,
  renderOption,
  onLoadMore,
  hasNextPage,
  isFetchingNextPage,
  isLoading = false,
  maxSelections,
  showCheckbox = true,
  showClearButton = true,
  showSearch = true,
  onSearch,
  usePortal = false,
  minDropdownHeight,
  valueClassName,
  getItemUrl,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>('bottom');
  // Start hidden so the portal is invisible on the first render (before position
  // is computed by updatePortalPosition). This prevents the one-frame flicker
  // where the dropdown appears at position 0,0 before snapping to the correct place.
  const [portalStyle, setPortalStyle] = useState<React.CSSProperties>({ visibility: 'hidden' });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const portalDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isInsideTrigger = dropdownRef.current?.contains(target);
      const isInsidePortal = portalDropdownRef.current?.contains(target);

      // Check if click is on any scrollbar (page scrollbar or dropdown scrollbar)
      if (target instanceof Element) {
        const rect = target.getBoundingClientRect();
        const isOnScrollbar =
          event.clientX > rect.left + target.clientWidth ||
          event.clientY > rect.top + target.clientHeight;
        if (isOnScrollbar) {
          return;
        }
      }

      if (!isInsideTrigger && !isInsidePortal) {
        setIsOpen(false);
      }
    };

    // Defer listener registration by one tick so the click that opened the
    // dropdown (mousedown) has already finished bubbling before we start
    // listening. Without this, on the very first open after a page refresh,
    // the mousedown fires on the trigger button, React synchronously sets
    // isOpen=true, the newly-registered listener immediately receives the
    // still-bubbling event, sees the target as "outside" (DOM not yet
    // updated), and closes the dropdown in the same frame.
    const timerId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timerId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);


  // Find the visible bottom limit for portal dropdowns (clamped by modal or viewport)
  const getVisibleBottom = (el: HTMLElement): number => {
    let bottom = window.innerHeight;
    let parent = el.parentElement;
    while (parent && parent !== document.body) {
      const s = window.getComputedStyle(parent);
      if (/auto|scroll/.test(s.overflowY) || /auto|scroll/.test(s.overflow)) {
        bottom = Math.min(bottom, parent.getBoundingClientRect().bottom);
        break;
      }
      parent = parent.parentElement;
    }
    return bottom;
  };

  const updatePortalPosition = () => {
    if (!dropdownRef.current) return;
    const buttonRect = dropdownRef.current.getBoundingClientRect();
    const MARGIN = 8;
    const visibleBottom = getVisibleBottom(dropdownRef.current);
    const spaceBelow = visibleBottom - buttonRect.bottom - MARGIN;
    const spaceAbove = buttonRect.top - MARGIN;
    const opensAbove = spaceBelow < 80 && spaceAbove > spaceBelow;

    setDropdownPosition(opensAbove ? 'top' : 'bottom');

    if (usePortal) {
      const minH = minDropdownHeight ?? 60;
      if (opensAbove) {
        setPortalStyle({
          position: 'fixed',
          bottom: window.innerHeight - buttonRect.top + 2,
          left: buttonRect.left,
          width: buttonRect.width,
          zIndex: 9999,
          maxHeight: Math.max(minH, spaceAbove),
          visibility: 'visible',
        });
      } else {
        setPortalStyle({
          position: 'fixed',
          top: buttonRect.bottom + 2,
          left: buttonRect.left,
          width: buttonRect.width,
          zIndex: 9999,
          maxHeight: Math.max(minH, spaceBelow),
          visibility: 'visible',
        });
      }
    }
  };

  // useLayoutEffect runs synchronously after DOM mutations but BEFORE the
  // browser paints — ensuring position is computed and the portal is made
  // visible in a single paint cycle, eliminating the flicker that occurred
  // when useEffect (post-paint) was used.
  useLayoutEffect(() => {
    if (isOpen) {
      updatePortalPosition();
    } else {
      // Reset to hidden so the next open starts invisible until position is computed.
      setPortalStyle({ visibility: 'hidden' });
    }
  }, [isOpen, usePortal]); // eslint-disable-line react-hooks/exhaustive-deps

  // When the portal dropdown is open, recompute its position on any scroll or
  // resize so it tracks the trigger element even if the modal/page scrolls.
  // Also close the dropdown if the trigger scrolls out of the visible viewport.
  useEffect(() => {
    if (!isOpen || !usePortal) return;
    const handleRepositionEvent = () => {
      if (dropdownRef.current) {
        const rect = dropdownRef.current.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > window.innerHeight) {
          setIsOpen(false);
          return;
        }
      }
      updatePortalPosition();
    };
    // Capture phase so we catch scroll events on any ancestor (modal, page, etc.)
    window.addEventListener('scroll', handleRepositionEvent, true);
    window.addEventListener('resize', handleRepositionEvent);
    return () => {
      window.removeEventListener('scroll', handleRepositionEvent, true);
      window.removeEventListener('resize', handleRepositionEvent);
    };
  }, [isOpen, usePortal]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleScroll = () => {
    if (listRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = listRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 5 && hasNextPage && !isFetchingNextPage) {
        onLoadMore?.();
      }
    }
  };

  // When onSearch is provided, the caller handles filtering server-side — skip client-side filter.
  // ECOPD #46: split the search term on whitespace and require every token
  // to appear somewhere in the option name. Previously a single-substring
  // match meant "Test Dr" only matched contiguous "Test Dr" strings (e.g.
  // "Test Driver"), and missed options like "Test 123 Driver".
  const filteredOptions = onSearch
    ? options
    : (() => {
        const tokens = searchTerm
          .toLowerCase()
          .split(/\s+/)
          .filter(Boolean);
        if (tokens.length === 0) return options;
        return options.filter(option => {
          const name = option.name.toLowerCase();
          return tokens.every(tok => name.includes(tok));
        });
      })();

  const handleSelect = (option: Option) => {
    if (disabled) return;
    if (multiple) {
      const currentlySelected = Array.isArray(value) ? value : [];
      const isSelected = currentlySelected.some(item => item.id === option.id);
      
      if (isSelected) {
        // Always allow deselection
        onChange(currentlySelected.filter(item => item.id !== option.id));
      } else {
        // Only allow selection if under the limit
        if (!maxSelections || currentlySelected.length < maxSelections) {
          onChange([...currentlySelected, option]);
        }
      }
    } else {
      onChange(option);
      setIsOpen(false);
    }
    setSearchTerm('');
  };

  const handleClear = () => {
    if (disabled) return;
    if (multiple) {
      onChange([]);
    } else {
      onChange(null);
    }
    setIsOpen(false);
    setSearchTerm('');
  }

  return (
    <div id={id} className="relative" ref={dropdownRef} data-testid={dataTestId}>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        className={`w-full min-h-[36px] h-auto border border-border-strong rounded-[4px] text-left flex justify-between items-start py-[6px] px-[10px] text-[14px] text-foreground bg-surface-secondary hover:border-primary focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary focus:border-transparent ${disabled ? 'bg-surface-alt cursor-not-allowed opacity-60' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={(event) => {
          if (disabled) {
            return;
          }

          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setIsOpen(!isOpen);
          }
        }}
        data-testid={`${dataTestId}-button`}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <div className="flex-1 flex flex-wrap gap-1 items-center pr-8">
          {multiple && Array.isArray(value) && value.length > 0 ? (
            <MultiSelectChips
              value={value}
              onChange={(nextValue) => onChange(nextValue)}
              options={options}
              placeholder={placeholder}
              disabled={disabled}
            />
          ) : !multiple && value && !Array.isArray(value) ? (
            <span className={valueClassName}>{value.name}</span>
          ) : (
            <span className="text-text-muted">{placeholder}</span>
          )}
        </div>
        <ChevronDown size={16} className={`transform transition-transform flex-shrink-0 ml-2 absolute right-[10px] top-[10px] ${isOpen ? 'rotate-180' : ''}`} />
      </div>

      {isOpen && !disabled && (() => {
        const dropdownContent = (
          <div
            ref={usePortal ? portalDropdownRef : undefined}
            className={`bg-surface border border-border-strong rounded-md shadow-lg flex flex-col ${
              usePortal
                ? ''
                : dropdownPosition === 'top'
                  ? 'absolute z-50 min-w-full w-full bottom-full mb-1'
                  : 'absolute z-50 min-w-full w-full top-full mt-1'
            }`}
            style={usePortal ? { ...portalStyle, overflow: 'hidden' } : undefined}
          >
            {showSearch && (
              <div className="p-2">
                <div className="relative">
                  <Input
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      onSearch?.(e.target.value);
                    }}
                    className="pl-8"
                    data-testid={`${dataTestId}-search-input`}
                  />
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-text-muted" size={16} />
                </div>
              </div>
            )}
            {showClearButton && (
              <Button
                variant={ButtonVariant.Ghost}
                size={ButtonSize.Sm}
                onClick={handleClear}
                className="w-full text-left px-3 py-2 text-sm text-primary hover:bg-surface-alt justify-start border-t border-b border-border rounded-none"
                data-testid={`${dataTestId}-clear-button`}
              >
                Clear
              </Button>
            )}
            <ul ref={listRef} onScroll={handleScroll} className="max-h-60 overflow-y-auto">
              {filteredOptions.map(option => {
                const currentlySelected = Array.isArray(value) ? value : [];
                const isSelected = multiple
                  ? currentlySelected.some(v => v.id === option.id)
                  : !!(value && !Array.isArray(value) && value.id === option.id);
                const isDisabled = !!(multiple && maxSelections && !isSelected && currentlySelected.length >= maxSelections);

                const optionUrl = getItemUrl ? getItemUrl(option) : undefined;
                const isLinkable = !!optionUrl;
                const baseClass = `px-4 py-2 flex items-center text-[0.85rem] leading-5 w-full text-left no-underline ${
                  isDisabled
                    ? 'cursor-not-allowed opacity-50'
                    : isSelected
                      ? 'text-primary hover:bg-surface-alt cursor-pointer'
                      : isLinkable
                        ? 'text-tableBlueColor hover:text-text hover:bg-surface-alt cursor-pointer'
                        : 'hover:bg-surface-alt cursor-pointer'
                }`;

                const innerContent = (
                  <>
                    {showCheckbox && (
                      <input
                        type="checkbox"
                        readOnly
                        checked={
                          multiple
                            ? isSelected
                            : !!(value && !Array.isArray(value) && value.id === option.id)
                        }
                        disabled={isDisabled}
                        className="mr-2"
                        tabIndex={-1}
                      />
                    )}
                    {renderOption ? renderOption(option) : (
                      <span className="flex flex-col">
                        <span>{option.name}</span>
                        {option.sublabel && (
                          <span className="text-xs text-text-muted">{option.sublabel}</span>
                        )}
                      </span>
                    )}
                  </>
                );

                return (
                  <li key={option.id}>
                    {optionUrl ? (
                      <a
                        href={optionUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={baseClass}
                        tabIndex={isDisabled ? -1 : 0}
                        aria-disabled={isDisabled}
                        data-testid={`${dataTestId}-option-${option.id}`}
                        onClick={(e) => {
                          // Cmd/Ctrl/Shift/middle-click → fall through to browser (opens new tab).
                          // Plain left-click → toggle selection without navigating.
                          if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) return;
                          e.preventDefault();
                          if (!isDisabled) handleSelect(option);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            if (!isDisabled) handleSelect(option);
                          }
                        }}
                      >
                        {innerContent}
                      </a>
                    ) : (
                      <div
                        role="button"
                        tabIndex={isDisabled ? -1 : 0}
                        aria-disabled={isDisabled}
                        className={baseClass}
                        onClick={(e) => {
                          // Cmd/Ctrl/Shift/middle-click should not toggle selection.
                          if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) return;
                          if (!isDisabled) handleSelect(option);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            if (!isDisabled) handleSelect(option);
                          }
                        }}
                        data-testid={`${dataTestId}-option-${option.id}`}
                      >
                        {innerContent}
                      </div>
                    )}
                  </li>
                );
              })}
              {(isLoading || isFetchingNextPage) && (
                <li className="text-center p-2 text-text-muted">{t('common.searching', 'Searching...')}</li>
              )}
              {!isLoading && !isFetchingNextPage && filteredOptions.length === 0 && (
                <li className="text-center p-2 text-text-muted">{t('common.noResults')}</li>
              )}
            </ul>
          </div>
        );

        return usePortal
          ? ReactDOM.createPortal(dropdownContent, document.body)
          : dropdownContent;
      })()}
    </div>
  );
};
